import { useMemo, useState } from 'react';
import { useAdminOrg } from '@/hooks/useAdminOrg';
import { useReadOnlyGuard } from '@/hooks/useReadOnlyGuard';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, CreditCard, Loader2, Minus, Package, ShieldAlert, Sparkles } from 'lucide-react';
import {
  AdminPage,
  EmptyState,
  IconTile,
  PageHeader,
  Panel,
  StatusChip,
  adminButton,
  adminTabs,
  useAdminPageTitle,
  type AdminTone,
} from '@/components/admin/ui';
import { cn } from '@/lib/utils';
import {
  type BillingInterval,
  type OrgSubscription,
  type SubscriptionPrice,
  type SubscriptionPlan,
  type SubscriptionStatus,
  useOrgSubscription,
  useSubscriptionCheckout,
  useSubscriptionPrices,
} from '@/hooks/useOrgSubscription';
import { useOrgPlanLimits } from '@/hooks/useOrgPlanLimits';
import { PLAN_LIMITS, planIncludes } from '@/lib/plan-limits';

// Plan keys are fixed in the database; these are the names on the pricing page.
const PLAN_LABEL: Record<SubscriptionPlan, string> = {
  starter: 'Solo',
  pro: 'Team',
  business: 'Business',
};

// Every plan has bookings, enquiries and the page builder; the limits that
// differ are listed from PLAN_LIMITS (src/lib/plan-limits.ts).
const PLAN_BLURB: Record<SubscriptionPlan, string> = {
  starter: 'For a one-person business on its own subdomain.',
  pro: 'For a small team with their own logins.',
  business: 'For growing teams, with priority support.',
};

const PLAN_ORDER: SubscriptionPlan[] = ['starter', 'pro', 'business'];

/** Stripe statuses that mean the org already has a subscription to manage. */
const LIVE_STRIPE_STATUSES = new Set(['trialing', 'active', 'past_due', 'unpaid', 'incomplete', 'paused']);

const STATUS_TONE: Record<SubscriptionStatus, AdminTone> = {
  trialing: 'violet',
  active: 'green',
  past_due: 'rose',
  canceled: 'rose',
  incomplete: 'amber',
};

const STATUS_LABEL: Record<SubscriptionStatus, string> = {
  trialing: 'Trialing',
  active: 'Active',
  past_due: 'Past due',
  canceled: 'Canceled',
  incomplete: 'Incomplete',
};

/** Plan label that tolerates plan keys outside the known catalogue. */
const planLabel = (plan: string | null | undefined) =>
  plan ? PLAN_LABEL[plan as SubscriptionPlan] ?? plan.charAt(0).toUpperCase() + plan.slice(1) : 'No plan';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function currencySymbol(currency: string): string {
  return currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : currency === 'USD' ? '$' : '';
}

function formatMoney(amount: number, currency: string): string {
  const n = Number(amount);
  return `${currencySymbol(currency)}${Number.isInteger(n) ? n.toFixed(0) : n.toFixed(2)}`;
}

/** Whether the org has a Stripe subscription (so plan changes go via the portal). */
function hasLiveSubscription(sub: OrgSubscription | null | undefined): boolean {
  if (!sub?.stripe_subscription_id) return false;
  return LIVE_STRIPE_STATUSES.has(sub.stripe_status ?? sub.status);
}

/** Whether a plan card is the org's current, paid-for plan at this interval. */
function isCurrentPrice(sub: OrgSubscription | null | undefined, price: SubscriptionPrice): boolean {
  if (!hasLiveSubscription(sub)) return false;
  return sub!.stripe_price_id ? sub!.stripe_price_id === price.stripe_price_id : sub!.plan === price.plan;
}

/** True while the org's free trial is still running and it has not subscribed. */
function inFreeTrial(sub: OrgSubscription | null | undefined): boolean {
  if (!sub || hasLiveSubscription(sub) || !sub.trial_ends_at) return false;
  return new Date(sub.trial_ends_at).getTime() > Date.now();
}

const BillingAdmin = () => {
  useAdminPageTitle('Billing & Subscription');
  const { orgId, loading: orgLoading, canWrite } = useAdminOrg();
  const { assertCanWrite } = useReadOnlyGuard();
  const { isOrgOwner, isPlatformAdmin } = useAuth();
  const { toast } = useToast();
  const canManage = canWrite && (isOrgOwner || isPlatformAdmin);

  const { data: subscription, isLoading: subLoading } = useOrgSubscription(orgId);
  const { data: prices, isLoading: pricesLoading } = useSubscriptionPrices();
  const checkout = useSubscriptionCheckout();
  const { data: planLimits } = useOrgPlanLimits(orgId);
  const teamMemberCount = planLimits?.teamMemberCount ?? null;

  const [notConfigured, setNotConfigured] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | 'portal' | null>(null);
  const [interval, setBillingInterval] = useState<BillingInterval>('month');

  const subscribed = hasLiveSubscription(subscription);
  const trialRunning = inFreeTrial(subscription);
  const hasAnnual = Boolean(prices?.some((p) => p.billing_interval === 'year'));
  const plans = useMemo(() => {
    const byPlan = new Map<SubscriptionPlan, { month?: SubscriptionPrice; year?: SubscriptionPrice }>();
    for (const price of prices ?? []) {
      const entry = byPlan.get(price.plan) ?? {};
      entry[price.billing_interval] = price;
      byPlan.set(price.plan, entry);
    }
    return PLAN_ORDER.flatMap((plan) => {
      const entry = byPlan.get(plan);
      const price = entry?.[interval] ?? entry?.month ?? entry?.year;
      return price ? [{ plan, price, monthly: entry?.month }] : [];
    });
  }, [prices, interval]);

  const start = async (mode: 'checkout' | 'portal', plan?: SubscriptionPlan) => {
    if (!assertCanWrite()) return;
    if (!canManage) return;
    if (!orgId) return;
    setNotConfigured(false);
    setPendingPlan(plan ?? 'portal');
    try {
      const result = await checkout.mutateAsync({ organizationId: orgId, plan, interval, mode });
      if (result.configured && result.url) {
        window.location.href = result.url;
        return;
      }
      // Fail safe: Stripe / the price catalogue isn't set up server-side.
      setNotConfigured(true);
      toast({
        title: 'Billing not configured',
        description:
          'Online billing isn’t configured yet. Please contact support to enable plan subscriptions.',
        variant: 'destructive',
      });
    } catch (error) {
      toast({
        title: 'Could not start billing',
        description: (error as Error).message,
        variant: 'destructive',
      });
    } finally {
      setPendingPlan(null);
    }
  };

  const loading = orgLoading || subLoading;

  const periodLine = subscription?.cancel_at_period_end && subscription.current_period_end
    ? `Ends ${formatDate(subscription.current_period_end)}`
    : subscribed && subscription?.status === 'trialing'
      ? `Free until ${formatDate(subscription.trial_ends_at)}, then billed automatically`
      : trialRunning
        ? `Free trial ends ${formatDate(subscription?.trial_ends_at)}`
        : subscribed && subscription?.current_period_end
          ? `Renews ${formatDate(subscription.current_period_end)}`
          : subscription?.status === 'trialing'
            ? `Free trial ended ${formatDate(subscription.trial_ends_at)}. Choose a plan to keep going.`
            : 'Choose a plan below to subscribe.';

  return (
    <AdminPage width="narrow">
      <PageHeader description="Manage your plan, trial, and payment details for this organisation." />

      {loading ? (
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading subscription…
        </div>
      ) : (
        <>
          {/* Current subscription summary */}
          <Panel>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3.5">
                <IconTile tone="violet" className="h-11 w-11 rounded-xl [&_svg]:h-5 [&_svg]:w-5">
                  <Sparkles />
                </IconTile>
                <div className="min-w-0">
                  <p className="adm-label">Current plan</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <h2 className="text-[20px] font-semibold leading-tight tracking-tight text-foreground">
                      {subscribed ? planLabel(subscription?.plan) : subscription?.status === 'trialing' ? 'Free trial' : planLabel(subscription?.plan)}
                    </h2>
                    {subscription && (
                      <StatusChip tone={STATUS_TONE[subscription.status] ?? 'grey'}>
                        {STATUS_LABEL[subscription.status] ?? subscription.status}
                      </StatusChip>
                    )}
                  </div>
                  <p className="mt-1 text-[12.5px] text-muted-foreground">{periodLine}</p>
                </div>
              </div>
              {canManage && subscription?.stripe_customer_id && (
                <Button
                  size="sm"
                  variant="ghost"
                  className={adminButton.outline}
                  disabled={!canManage || (checkout.isPending && pendingPlan === 'portal')}
                  onClick={() => start('portal')}
                >
                  {checkout.isPending && pendingPlan === 'portal' ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <CreditCard />
                  )}
                  Manage billing
                </Button>
              )}
            </div>

            {(subscription?.cancel_at_period_end || subscription?.status === 'past_due') && (
              <div className="mt-4 space-y-2 border-t border-border pt-4">
                {subscription?.cancel_at_period_end && (
                  <p className="flex items-center gap-2 text-[13px] text-destructive">
                    <ShieldAlert className="h-4 w-4 shrink-0" />
                    Your subscription is set to cancel at the end of the current period.
                  </p>
                )}
                {subscription?.status === 'past_due' && (
                  <p className="flex items-center gap-2 text-[13px] text-destructive">
                    <ShieldAlert className="h-4 w-4 shrink-0" />
                    A recent payment failed. Please update your payment method to avoid losing access.
                  </p>
                )}
              </div>
            )}
          </Panel>

          {notConfigured && (
            <div className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/[0.06] p-4 text-[13px] text-destructive">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Online billing is not configured yet, so plan changes can’t be processed here. Please
                contact support to enable subscriptions.
              </p>
            </div>
          )}

          {/* Plan catalogue */}
          <Panel
            title={subscribed ? 'Change plan' : 'Choose a plan'}
            description={
              subscribed
                ? 'Switching takes you to Stripe, where any difference is worked out for you.'
                : trialRunning
                  ? `Add your card now and you won’t be charged until ${formatDate(subscription?.trial_ends_at)}.`
                  : 'No contracts. Cancel any time from this page.'
            }
            actions={
              hasAnnual ? (
                <Tabs value={interval} onValueChange={(v) => setBillingInterval(v as BillingInterval)}>
                  <TabsList className={adminTabs.list} aria-label="Billing period">
                    <TabsTrigger value="month" className={adminTabs.trigger}>
                      Monthly
                    </TabsTrigger>
                    <TabsTrigger value="year" className={adminTabs.trigger}>
                      Annual
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              ) : undefined
            }
          >
            {pricesLoading ? (
              <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading plans…
              </div>
            ) : plans.length === 0 ? (
              <EmptyState
                compact
                icon={<Package />}
                title="No plans are available to subscribe to right now."
                description="Check back soon, or contact support if you expected to see plans here."
              />
            ) : (
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {plans.map(({ plan, price, monthly }) => {
                  const current = isCurrentPrice(subscription, price);
                  const label = PLAN_LABEL[plan];
                  const saving =
                    price.billing_interval === 'year' && monthly
                      ? Math.round(Number(monthly.unit_amount) * 12 - Number(price.unit_amount))
                      : 0;
                  return (
                    <div
                      key={price.id}
                      data-testid={`plan-${plan}`}
                      className={cn(
                        'flex flex-col rounded-xl border p-4 transition-shadow',
                        current
                          ? 'border-primary/40 bg-secondary/50 shadow-[0_0_0_3px_hsl(var(--primary)/0.08)]'
                          : 'border-border bg-card hover:shadow-[var(--adm-shadow-sm)]',
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-[14px] font-semibold text-foreground">{label}</h3>
                        {current && (
                          <StatusChip tone="violet" dot={false}>
                            <Sparkles className="h-3 w-3" /> Current
                          </StatusChip>
                        )}
                      </div>
                      <p className="mt-3 flex items-baseline gap-1">
                        <span className="adm-num text-[26px] font-semibold leading-none text-foreground">
                          {formatMoney(price.unit_amount, price.currency_code)}
                        </span>
                        <span className="text-[12.5px] text-muted-foreground">/{price.billing_interval}</span>
                      </p>
                      <p className="mt-1.5 flex min-h-[18px] items-center gap-1.5 text-[12px] text-muted-foreground">
                        {saving > 0 && (
                          <>
                            <Check className="h-3.5 w-3.5 text-primary" />
                            Save {formatMoney(saving, price.currency_code)} a year
                          </>
                        )}
                      </p>
                      <p className="mt-2 text-[12.5px] leading-snug text-muted-foreground">{PLAN_BLURB[plan]}</p>
                      <ul className="mt-3 flex-1 space-y-1.5 text-[12.5px]" data-testid={`plan-${plan}-includes`}>
                        {planIncludes(plan).map((item) => (
                          <li
                            key={item.label}
                            className={cn('flex items-center gap-2', item.included ? 'text-foreground' : 'text-muted-foreground')}
                          >
                            {item.included ? (
                              <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                            ) : (
                              <Minus className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                            )}
                            <span>{item.label}</span>
                          </li>
                        ))}
                      </ul>
                      {teamMemberCount !== null &&
                        PLAN_LIMITS[plan].maxTeamMembers !== null &&
                        teamMemberCount > PLAN_LIMITS[plan].maxTeamMembers! && (
                          <p className="mt-3 text-[12px] leading-snug text-amber-700 dark:text-amber-300">
                            You have {teamMemberCount} team members. Everyone stays, but you can’t add more on {label}.
                          </p>
                        )}
                      <Button
                        size="sm"
                        variant={current ? 'ghost' : 'default'}
                        className={cn('mt-4 w-full', current ? adminButton.outline : adminButton.primary)}
                        disabled={!canManage || current || (checkout.isPending && pendingPlan === plan)}
                        onClick={() => (subscribed ? start('portal') : start('checkout', plan))}
                      >
                        {checkout.isPending && pendingPlan === plan && <Loader2 className="animate-spin" />}
                        {current ? 'Current plan' : subscribed ? `Switch to ${label}` : `Choose ${label}`}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}

            {!canManage && (
              <p className="mt-4 text-[12.5px] text-muted-foreground">
                Only the organisation owner can change the subscription.
              </p>
            )}
          </Panel>
        </>
      )}
    </AdminPage>
  );
};

export default BillingAdmin;
